import { type Type } from '@angular/core';
import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementBase,
  type CanvasElementFeatures,
  type CanvasElementId,
  type CanvasElementType,
  type ElementAvailability,
  type ElementGroupId,
  type ElementMetaPaths,
  type IoConfigOf,
  type PartialPropsOf,
  type PropsOf,
  type SlotId,
} from '@blaze-runtime/engine-domain';
import { type PropertiesConfig, type ToolbarToolTypes } from '@blaze-shared/elements-domain';

import type { DefinitionMapper, ElementHandler } from '../ai';
import type { CanvasElementGeneration } from '../elements/__base/generation';
import type { ElementMigration } from '../services/compat/migrations/layout-migration.models';

import { type CanvasElementComponent } from './element-component';
import {
  type ComponentChildrenHandler,
  type ComponentValidationHandler,
  type OutputEmitGuard,
} from './io-data/io-data.models';
import { type ParentChildrenOverrides } from './parent-overrides';

export type CanvasElementComponentType<T extends CanvasElementType = CanvasElementType> = Type<
  CanvasElementComponent<T>
>;

export interface CanvasElementDefinition<
  T extends CanvasElementType = CanvasElementType,
  TVariant extends CanvasElementType = T,
> extends CanvasElementBase<T> {
  /** Is listed on components list? */
  isListed: boolean;

  /** Displayed name */
  name: string;

  /** Component class */
  componentType: CanvasElementComponentType<TVariant>;

  groups?: ElementGroupId[];

  /** Inputs and outputs */
  ioConfig?: IoConfigOf<T>;

  /** Properties Editor config */
  propsConfig?: PropertiesConfig;

  /**
   * Default element model - not saved in BE;
   * Changing these values will affect existing elements
   * If safe improvements are needed, use `initial` instead
   * to define overrides affecting the newly created elements only
   */
  defaults?: PropsOf<T>;

  /** Initial element model - copied to elements */
  initial?: PartialDeep<PropsOf<T>>;

  /** Explicit generation hooks for host-specific generated instances of the element. */
  generation?: CanvasElementGeneration<T>;

  /** Properties Editor config for slot */
  slotPropsConfig?: (slotId: SlotId) => PropertiesConfig;

  /** Default props for slot */
  slotDefaults?: PropsOf<T>;

  /** Initial slot model */
  slotInitial?: PartialDeep<PropsOf<T>>;

  /** Additional mini-toolbar actions */
  toolbar?: ToolbarToolTypes[];

  /** Element uses the same component with a separate set of props & config options */
  variantOf?: TVariant;

  /** Keywords used for search purposes */
  tags?: string[];

  /** Minimum access requirement to see the component listed */
  availableFor?: ElementAvailability;

  /** Element visible only in Customer Portal projects */
  isCustomerPortalOnly?: boolean;

  /** Which properties are considered as element's style */
  styleIncludePaths?: string[];

  /** List of excluded properties that are included in styleIncludePaths */
  styleExcludePaths?: string[];

  metaUiFeatures?: {
    type: 'input' | 'container' | 'button';
    paths?: ElementMetaPaths;
  };

  features?: CanvasElementFeatures;

  outputEmitGuard?: Type<OutputEmitGuard>;
  childrenHandler?: Type<ComponentChildrenHandler>;
  validationHandler?: Type<ComponentValidationHandler>;

  /**
   * Maps the element's own (merged) props to the overrides bundle applied onto `childId`
   * ("Style All Fields"). The child is named rather than described because only the parent
   * knows what role it granted which child: a form styles every field alike and ignores it,
   * while the wizard reads its step ids to tell a question's text from its explanation.
   * Returning undefined means this parent does not style that child.
   */
  childrenOverridesResolver?: (
    parentProps: PartialPropsOf<T>,
    childId: CanvasElementId,
  ) => ParentChildrenOverrides | undefined;
  ai?: {
    /**
     * AI-friendly name of the component
     * Can be different from then element's name/type
     */
    name: string;
    handler: () => Type<ElementHandler<T>>;
    definitionMapper: () => Type<DefinitionMapper<T>>;
  };

  /**
   * Current schema version of the element definition.
   * Used by layout migrations. Starts at 1.
   * If > 1, requires migration defined in `migrations` property.
   */
  version: number;
  /**
   * Migrations for the element definition. Each migration defines the migration logic from version n to version n+1.
   * Used by layout migrations to update elements of this type to the latest schema version.
   */
  migrations?: MigrationRegistry;
}

export type MigrationRegistry = Record<number, ElementMigration>;

/**
 * This is the same as CanvasElementDefinition,
 * but omits class/function fields such as {@link CanvasElementDefinition.componentType componentType} and {@link CanvasElementDefinition.propsBuilder propsBuilder}
 * to avoid running them through NgRx immutability checks which freeze the object.
 * The unfrozen {@link CanvasElementDefinition.componentType componentType} is needed for Angular's createComponent method.
 * {@link CanvasElementDefinition.propsBuilder propsBuilder} is used to generate and update props.
 *
 * **See more:** Use {@link stripClassTypes} to generate stripped definiton object.
 */
export type CanvasElementDefinitionInternal<T extends CanvasElementType = CanvasElementType> = Omit<
  CanvasElementDefinition<T>,
  | 'componentType'
  | 'propsBuilder'
  | 'generation'
  | 'slotPropsConfig'
  | 'ai'
  | 'childrenOverridesResolver'
> & {
  componentType: undefined;
  propsBuilder: undefined;
  generation: undefined;
  slotPropsConfig: undefined;
  ai: undefined;
  childrenOverridesResolver: undefined;
};

export function isElementDefinitionType<T extends CanvasElementType>(
  elementDefinition: CanvasElementDefinition<any> | undefined,
  type: T,
): elementDefinition is CanvasElementDefinition<T> {
  return elementDefinition !== undefined && elementDefinition.type === type;
}

export function isElementDefinitionTypeInternal<T extends CanvasElementType>(
  elementDefinition: CanvasElementDefinitionInternal<any> | undefined,
  type: T,
): elementDefinition is CanvasElementDefinitionInternal<T> {
  return elementDefinition !== undefined && elementDefinition.type === type;
}

/**
 * Creates modified definition with runtime-only class/function fields removed.
 * The component cannot be passed as payload in actions as Angular needs to be able to add its meta data to components definition.
 * This clashes with NgRx checks for action's payload immutability.
 * @param elementDefinition the definition to be used
 * @returns a new instance of the definition with runtime-only class/function fields removed
 */
export function stripClassTypes<T extends CanvasElementType>(
  elementDefinition: CanvasElementDefinition<T>,
): CanvasElementDefinitionInternal<T> {
  const definition: CanvasElementDefinitionInternal<T> = {
    ...elementDefinition,
    ai: undefined,
    componentType: undefined,
    propsBuilder: undefined,
    generation: undefined,
    propsExtractor: undefined,
    slotPropsConfig: undefined,
    outputEmitGuard: undefined,
    childrenHandler: undefined,
    validationHandler: undefined,
    migrations: undefined,
    childrenOverridesResolver: undefined,
  };
  delete definition.componentType;
  return definition;
}
